import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Plus,
  Users,
  User,
  AlertTriangle,
  CheckCircle,
  Check,
  X,
  Save,
  RefreshCw,
  FileDown
} from 'lucide-react';
import axios from 'axios';
import { usePermissions } from '../contexts/PermissionsContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.VITE_REACT_APP_BACKEND_URL;

// German Public Holidays Calculator
const getGermanPublicHolidays = (year) => {
  // Calculate Easter Sunday (Gauss algorithm)
  const calculateEaster = (year) => {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(year, month - 1, day);
  };

  const easter = calculateEaster(year);
  const holidays = [];

  // Fixed holidays
  holidays.push({ date: new Date(year, 0, 1), name: 'Neujahr' });
  holidays.push({ date: new Date(year, 0, 6), name: 'Heilige Drei Könige' });
  holidays.push({ date: new Date(year, 4, 1), name: 'Tag der Arbeit' });
  holidays.push({ date: new Date(year, 7, 15), name: 'Mariä Himmelfahrt' });
  holidays.push({ date: new Date(year, 9, 3), name: 'Tag der Deutschen Einheit' });
  holidays.push({ date: new Date(year, 9, 31), name: 'Reformationstag' });
  holidays.push({ date: new Date(year, 10, 1), name: 'Allerheiligen' });
  holidays.push({ date: new Date(year, 11, 25), name: '1. Weihnachtsfeiertag' });
  holidays.push({ date: new Date(year, 11, 26), name: '2. Weihnachtsfeiertag' });

  // Movable holidays (based on Easter)
  const karfreitag = new Date(easter);
  karfreitag.setDate(easter.getDate() - 2);
  holidays.push({ date: karfreitag, name: 'Karfreitag' });

  const ostermontag = new Date(easter);
  ostermontag.setDate(easter.getDate() + 1);
  holidays.push({ date: ostermontag, name: 'Ostermontag' });

  const christiHimmelfahrt = new Date(easter);
  christiHimmelfahrt.setDate(easter.getDate() + 39);
  holidays.push({ date: christiHimmelfahrt, name: 'Christi Himmelfahrt' });

  const pfingstmontag = new Date(easter);
  pfingstmontag.setDate(easter.getDate() + 50);
  holidays.push({ date: pfingstmontag, name: 'Pfingstmontag' });

  const fronleichnam = new Date(easter);
  fronleichnam.setDate(easter.getDate() + 60);
  holidays.push({ date: fronleichnam, name: 'Fronleichnam' });

  return holidays;
};

// Check if a date is a German public holiday
const isGermanPublicHoliday = (date) => {
  const year = date.getFullYear();
  const holidays = getGermanPublicHolidays(year);
  
  return holidays.some(holiday => 
    holiday.date.getFullYear() === date.getFullYear() &&
    holiday.date.getMonth() === date.getMonth() &&
    holiday.date.getDate() === date.getDate()
  );
};

// Get holiday name for a date
const getHolidayName = (date) => {
  const year = date.getFullYear();
  const holidays = getGermanPublicHolidays(year);
  
  const holiday = holidays.find(h => 
    h.date.getFullYear() === date.getFullYear() &&
    h.date.getMonth() === date.getMonth() &&
    h.date.getDate() === date.getDate()
  );
  
  return holiday ? holiday.name : null;
};

const Calendar = () => {
  const navigate = useNavigate();
  const { isTechniker, user } = usePermissions();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [dayDetails, setDayDetails] = useState(null);
  const [showDayModal, setShowDayModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [availableTeams, setAvailableTeams] = useState([]);
  const [viewMode, setViewMode] = useState('week'); // 'month' or 'week'
  const [weekData, setWeekData] = useState(null);
  const [technikerWorkDays, setTechnikerWorkDays] = useState([]); // Arbeitstage für Techniker
  const [vacations, setVacations] = useState([]); // Urlaubs- und Krankheitsdaten

  // Day modal state
  const [dailyOrders, setDailyOrders] = useState({
    connect_offtracks_total: 0,
    gwv_offtracks_total: 0,
    notes: ''
  });
  
  const [teamAssignments, setTeamAssignments] = useState([]);
  const [selectedTeamForAssignment, setSelectedTeamForAssignment] = useState(null);
  const [teamAssignmentForm, setTeamAssignmentForm] = useState({
    team_id: '',
    connect_offtracks: 0,
    gwv_offtracks: 0,
    notes: ''
  });

  // Billing state
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [billingPositions, setBillingPositions] = useState([]);
  const [selectedBillingDate, setSelectedBillingDate] = useState(null);
  const [billingData, setBillingData] = useState(null);
  const [billingOrders, setBillingOrders] = useState([]);
  const [supportTeams, setSupportTeams] = useState([]);

  useEffect(() => {
    if (viewMode === 'month') {
      loadCalendarMonth();
    } else {
      loadWeekData();
    }
    loadEmployeesAndTeams();
    
    // Techniker-Arbeitstage laden
    if (isTechniker()) {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      loadTechnikerWorkDays(year, month);
    }
  }, [currentDate, viewMode, isTechniker]);

  // Load billing positions
  useEffect(() => {
    if (isTechniker()) {
      loadBillingPositions();
    }
  }, []);

  const loadBillingPositions = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${BACKEND_URL}/api/billing-positions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBillingPositions(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Abrechnungspositionen:', error);
    }
  };

  const loadCalendarMonth = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      
      const response = await axios.get(`${BACKEND_URL}/api/calendar/month`, {
        params: { year, month },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCalendarData(response.data);
    } catch (error) {
      console.error('Fehler beim Laden des Kalenders:', error);
      alert('Fehler beim Laden der Kalenderdaten');
    } finally {
      setLoading(false);
    }
  };

  const loadEmployeesAndTeams = async () => {
    try {
      const token = localStorage.getItem('authToken');
      
      if (!isTechniker()) {
        // Nur für Admin/Manager: Load employees, teams, and vacations
        const [employeesResponse, teamsResponse, vacationsResponse] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/employees`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${BACKEND_URL}/api/teams`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${BACKEND_URL}/api/vacations`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        
        setEmployees(employeesResponse.data.filter(emp => emp.status === 'aktiv'));
        setAvailableTeams(teamsResponse.data);
        // Filter nur genehmigte Urlaube
        setVacations(vacationsResponse.data.filter(v => v.status === 'genehmigt'));
      } else {
        // Techniker: Load teams for support selection in billing
        const teamsResponse = await axios.get(`${BACKEND_URL}/api/teams`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAvailableTeams(teamsResponse.data);
      }
      
    } catch (error) {
      console.error('Fehler beim Laden der Daten:', error);
    }
  };

  const loadTechnikerWorkDays = async (year, month) => {
    if (!isTechniker()) return;
    
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${BACKEND_URL}/api/calendar/my-work-days`, {
        params: { year, month },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTechnikerWorkDays(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Techniker-Arbeitstage:', error);
      setTechnikerWorkDays([]);
    }
  };

  // Helper function to check if a day is a work day for the technician
  const isWorkDay = (dateStr) => {
    if (!isTechniker()) return false;
    return technikerWorkDays.some(wd => wd.date === dateStr);
  };

  // Get work day info for a specific date
  const getWorkDayInfo = (dateStr) => {
    if (!isTechniker()) return null;
    return technikerWorkDays.find(wd => wd.date === dateStr) || null;
  };

  // Handle billing for a work day
  const handleOpenBilling = async (dateStr) => {
    if (!isTechniker()) return;
    
    const workDayInfo = getWorkDayInfo(dateStr);
    if (!workDayInfo) {
      alert('Sie sind an diesem Tag keinem Team zugewiesen.');
      return;
    }

    setSelectedBillingDate(dateStr);
    
    // Load existing billing if available
    if (workDayInfo.has_billing) {
      try {
        const token = localStorage.getItem('authToken');
        const response = await axios.get(`${BACKEND_URL}/api/billings/${workDayInfo.billing_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setBillingData(response.data);
        setBillingOrders(response.data.orders || []);
        setSupportTeams(response.data.support_teams || []);
      } catch (error) {
        console.error('Fehler beim Laden der Abrechnung:', error);
        setBillingData(null);
        setBillingOrders([]);
        setSupportTeams([]);
      }
    } else {
      // Initialize new billing
      setBillingData({
        date: dateStr,
        employee_id: user.id,
        team_id: workDayInfo.team_id,
        team_number: workDayInfo.team_number,
        order_type: workDayInfo.order_type
      });
      setBillingOrders([]);
      setSupportTeams([]);
    }
    
    setShowBillingModal(true);
  };

  // Add a new order to billing
  const addBillingOrder = (orderType) => {
    const orderNumber = billingOrders.filter(o => o.order_type === orderType).length + 1;
    setBillingOrders([...billingOrders, {
      order_type: orderType,
      order_number: orderNumber,
      smile_number: '',
      positions: [],
      total: 0
    }]);
  };

  // Update order
  const updateBillingOrder = (index, field, value) => {
    const updatedOrders = [...billingOrders];
    updatedOrders[index] = { ...updatedOrders[index], [field]: value };
    setBillingOrders(updatedOrders);
  };

  // Add position to order
  const addPositionToOrder = (orderIndex, positionId) => {
    const position = billingPositions.find(p => p.id === positionId);
    if (!position) return;

    const updatedOrders = [...billingOrders];
    updatedOrders[orderIndex].positions.push({
      position_id: position.id,
      position_number: position.position_number,
      description: position.description,
      unit: position.unit,
      unit_price: position.unit_price,
      quantity: 1,
      subtotal: position.unit_price
    });
    
    // Recalculate order total
    updatedOrders[orderIndex].total = updatedOrders[orderIndex].positions.reduce(
      (sum, pos) => sum + pos.subtotal,
      0
    );
    
    setBillingOrders(updatedOrders);
  };

  // Update position quantity
  const updatePositionQuantity = (orderIndex, positionIndex, quantity) => {
    const updatedOrders = [...billingOrders];
    const position = updatedOrders[orderIndex].positions[positionIndex];
    position.quantity = parseFloat(quantity) || 0;
    position.subtotal = position.quantity * position.unit_price;
    
    // Recalculate order total
    updatedOrders[orderIndex].total = updatedOrders[orderIndex].positions.reduce(
      (sum, pos) => sum + pos.subtotal,
      0
    );
    
    setBillingOrders(updatedOrders);
  };

  // Remove position from order
  const removePositionFromOrder = (orderIndex, positionIndex) => {
    const updatedOrders = [...billingOrders];
    updatedOrders[orderIndex].positions.splice(positionIndex, 1);
    
    // Recalculate order total
    updatedOrders[orderIndex].total = updatedOrders[orderIndex].positions.reduce(
      (sum, pos) => sum + pos.subtotal,
      0
    );
    
    setBillingOrders(updatedOrders);
  };

  // Remove order
  const removeBillingOrder = (orderIndex) => {
    const updatedOrders = [...billingOrders];
    updatedOrders.splice(orderIndex, 1);
    setBillingOrders(updatedOrders);
  };

  // Calculate total amount
  const calculateTotalAmount = () => {
    return billingOrders.reduce((sum, order) => sum + order.total, 0);
  };

  // Save billing
  const saveBilling = async (confirmed = false) => {
    try {
      const token = localStorage.getItem('authToken');
      const workDayInfo = getWorkDayInfo(selectedBillingDate);
      
      // Use employee_id from user object (for employee logins)
      const employeeId = user.employee_id || user.id;
      
      const billingPayload = {
        date: selectedBillingDate,
        employee_id: employeeId,
        team_id: workDayInfo.team_id,
        team_number: workDayInfo.team_number,
        support_teams: supportTeams,
        orders: billingOrders,
        total_amount: calculateTotalAmount(),
        confirmed: confirmed
      };

      if (billingData && billingData.id) {
        // Update existing billing
        await axios.put(`${BACKEND_URL}/api/billings/${billingData.id}`, billingPayload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Abrechnung erfolgreich aktualisiert und an Buchhaltung gesendet!');
      } else {
        // Create new billing
        await axios.post(`${BACKEND_URL}/api/billings`, billingPayload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Abrechnung erfolgreich erstellt und an Buchhaltung gesendet!');
      }

      // Close modal
      setShowBillingModal(false);
      
      // Navigate to MyBillings page
      navigate('/my-billings');
    } catch (error) {
      console.error('Fehler beim Speichern der Abrechnung:', error);
      alert(error.response?.data?.detail || 'Fehler beim Speichern der Abrechnung');
    }
  };

  const getWeekDates = (date) => {
    console.log(`🗓️ GetWeekDates called with: ${date.toString()}`);
    
    const startOfWeek = new Date(date);
    // Ensure we're working with local time, not UTC
    startOfWeek.setHours(12, 0, 0, 0); // Set to noon to avoid timezone issues
    
    const day = startOfWeek.getDay(); // 0=Sunday, 1=Monday, ..., 6=Saturday
    console.log(`   Current day of week: ${day} (${startOfWeek.toLocaleDateString('de-DE', { weekday: 'long' })})`);
    
    // Calculate Monday as first day (ISO week)
    const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1); // Monday as first day
    startOfWeek.setDate(diff);
    
    console.log(`   Monday of this week: ${startOfWeek.toString()}`);
    
    const weekDates = [];
    for (let i = 0; i < 7; i++) {
      const currentDay = new Date(startOfWeek);
      currentDay.setDate(startOfWeek.getDate() + i);
      currentDay.setHours(12, 0, 0, 0); // Set to noon to avoid timezone issues
      
      console.log(`   Week day ${i}: ${currentDay.toLocaleDateString('de-DE')} (${currentDay.toLocaleDateString('de-DE', { weekday: 'long' })})`);
      weekDates.push(currentDay);
    }
    
    console.log(`   Total week dates generated: ${weekDates.length}`);
    return weekDates;
  };

  const getWeekNumber = (date) => {
    const tempDate = new Date(date);
    tempDate.setHours(0, 0, 0, 0);
    tempDate.setDate(tempDate.getDate() + 3 - (tempDate.getDay() + 6) % 7);
    const week1 = new Date(tempDate.getFullYear(), 0, 4);
    return 1 + Math.round(((tempDate.getTime() - week1.getTime()) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
  };

  const loadWeekData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      
      const weekDates = getWeekDates(currentDate);
      const weekData = {
        weekNumber: getWeekNumber(currentDate),
        year: currentDate.getFullYear(),
        startDate: weekDates[0],
        endDate: weekDates[6],
        days: []
      };

      // Load data for each day of the week
      for (let i = 0; i < weekDates.length; i++) {
        const date = weekDates[i];
        // Fix timezone issue: use local date formatting instead of UTC
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const dateString = `${year}-${month}-${day}`;
        
        try {
          const response = await axios.get(`${BACKEND_URL}/api/calendar/day/${dateString}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          const dayData = {
            date: date,
            day: date.getDate(),
            weekday: date.toLocaleDateString('de-DE', { weekday: 'short' }),
            is_workday: date.getDay() >= 1 && date.getDay() <= 5, // Monday to Friday
            connect_total: response.data.daily_assignment?.connect_offtracks_total || 0,
            connect_assigned: response.data.team_assignments?.reduce((sum, team) => sum + (team.connect_offtracks || 0), 0) || 0,
            gwv_total: response.data.daily_assignment?.gwv_offtracks_total || 0,
            gwv_assigned: response.data.team_assignments?.reduce((sum, team) => sum + (team.gwv_offtracks || 0), 0) || 0,
            team_assignments: response.data.team_assignments || []
          };
          
          // Simplified day loading log
          console.log(`📅 Day ${i} - ${date.toLocaleDateString('de-DE', { weekday: 'long' })} ${date.toLocaleDateString('de-DE')} - Teams: ${dayData.team_assignments?.length || 0}`);
          
          weekData.days.push(dayData);
        } catch (error) {
          // Day might not exist, create empty day
          weekData.days.push({
            date: date,
            day: date.getDate(),
            weekday: date.toLocaleDateString('de-DE', { weekday: 'short' }),
            is_workday: date.getDay() >= 1 && date.getDay() <= 5,
            connect_total: 0,
            connect_assigned: 0,
            gwv_total: 0,
            gwv_assigned: 0,
            team_assignments: []
          });
        }
      }
      
      setWeekData(weekData);
    } catch (error) {
      console.error('Fehler beim Laden der Wochendaten:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDayDetails = async (date) => {
    try {
      const token = localStorage.getItem('authToken');
      // Fix: Use local date formatting to avoid timezone offset
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;
      
      // Always load employees first (needed even for error cases)
      let currentEmployees = [];
      if (!isTechniker()) {
        try {
          const employeesResponse = await axios.get(`${BACKEND_URL}/api/employees`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          currentEmployees = employeesResponse.data.filter(emp => emp.status === 'aktiv');
        } catch (empError) {
          console.error('Error loading employees:', empError);
        }
      }
      
      // Load day data
      const dayResponse = await axios.get(`${BACKEND_URL}/api/calendar/day/${dateString}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = dayResponse.data;
      
      console.log('🔍 Loading day details:', {
        date: dateString,
        teamAssignments: data.team_assignments,
        employeesCount: currentEmployees.length,
        hasDailyAssignment: !!data.daily_assignment
      });
      
      setDayDetails(data);
      
      // Set daily orders - handle case when daily_assignment doesn't exist yet
      setDailyOrders({
        connect_offtracks_total: data.daily_assignment?.connect_offtracks_total || 0,
        gwv_offtracks_total: data.daily_assignment?.gwv_offtracks_total || 0,
        notes: data.daily_assignment?.notes || ''
      });
      
      // Set team assignments with resolved employee objects
      const resolvedTeamAssignments = (data.team_assignments || []).map(team => {
        console.log('🔍 Resolving team:', {
          teamNumber: team.team_number,
          employee1_id: team.employee1_id,
          employee2_id: team.employee2_id
        });
        
        // Resolve employee1
        let employee1 = null;
        if (team.employee1_id) {
          const emp = currentEmployees.find(e => e.id === team.employee1_id);
          console.log('  - Looking for employee1:', team.employee1_id, emp ? `Found: ${emp.first_name} ${emp.last_name}` : 'NOT FOUND');
          if (emp) {
            employee1 = {
              id: emp.id,
              name: `${emp.first_name} ${emp.last_name}`,
              employee_number: emp.employee_number
            };
          }
        }
        
        // Resolve employee2
        let employee2 = null;
        if (team.employee2_id) {
          const emp = currentEmployees.find(e => e.id === team.employee2_id);
          console.log('  - Looking for employee2:', team.employee2_id, emp ? `Found: ${emp.first_name} ${emp.last_name}` : 'NOT FOUND');
          if (emp) {
            employee2 = {
              id: emp.id,
              name: `${emp.first_name} ${emp.last_name}`,
              employee_number: emp.employee_number
            };
          }
        }
        
        return {
          ...team,
          employee1,
          employee2
        };
      });
      
      console.log('🔍 Resolved team assignments:', resolvedTeamAssignments);
      setTeamAssignments(resolvedTeamAssignments);
      
    } catch (error) {
      console.error('Fehler beim Laden der Tagesdetails:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      
      // For future days or days without data, show modal anyway with empty data
      if (error.response?.status === 404 || error.response?.status === 500) {
        console.log('ℹ️ Day has no data yet, opening with empty state');
        
        // Set empty day details
        setDayDetails({
          weekday: date.toLocaleDateString('de-DE', { weekday: 'long' }),
          is_workday: date.getDay() >= 1 && date.getDay() <= 5,
          daily_assignment: null,
          team_assignments: [],
          available_employees: []
        });
        
        // Set empty daily orders
        setDailyOrders({
          connect_offtracks_total: 0,
          gwv_offtracks_total: 0,
          notes: ''
        });
        
        // Set empty team assignments
        setTeamAssignments([]);
        
        // Don't show error alert for missing data
        return;
      }
      
      alert('Fehler beim Laden der Tagesdetails');
    }
  };

  // PDF-Export-Funktionen
  const handleExportDayPDF = async () => {
    try {
      const token = localStorage.getItem('authToken');
      // Get the current selected day or the first day of the current week
      let exportDate = selectedDay;
      if (!exportDate && weekData) {
        exportDate = weekData.startDate.toISOString().split('T')[0];
      } else if (!exportDate) {
        exportDate = currentDate.toISOString().split('T')[0];
      }
      
      const response = await axios.get(`${BACKEND_URL}/api/calendar/export/pdf/day`, {
        params: { date: exportDate },
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `kalender_tag_${exportDate}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      alert('PDF erfolgreich exportiert!');
    } catch (error) {
      console.error('Fehler beim PDF-Export:', error);
      alert('Fehler beim PDF-Export');
    }
  };

  const handleExportWeekPDF = async () => {
    try {
      const token = localStorage.getItem('authToken');
      // Get the start date of the current week
      let exportDate;
      if (weekData) {
        exportDate = weekData.startDate.toISOString().split('T')[0];
      } else {
        // Calculate Monday of current week
        const tempDate = new Date(currentDate);
        const dayOfWeek = tempDate.getDay();
        const diff = tempDate.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
        tempDate.setDate(diff);
        exportDate = tempDate.toISOString().split('T')[0];
      }
      
      const response = await axios.get(`${BACKEND_URL}/api/calendar/export/pdf/week`, {
        params: { date: exportDate },
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `kalender_woche_${exportDate}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      alert('PDF erfolgreich exportiert!');
    } catch (error) {
      console.error('Fehler beim Wochen-PDF-Export:', error);
      alert('Fehler beim Wochen-PDF-Export');
    }
  };


  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const nextMonth = () => {
    const nextDate = new Date(currentDate);
    nextDate.setMonth(nextDate.getMonth() + 1);
    setCurrentDate(nextDate);
  };

  const prevMonth = () => {
    const prevDate = new Date(currentDate);
    prevDate.setMonth(prevDate.getMonth() - 1);
    setCurrentDate(prevDate);
  };

  const nextWeek = () => {
    const nextDate = new Date(currentDate);
    nextDate.setDate(nextDate.getDate() + 7);
    setCurrentDate(nextDate);
  };

  const prevWeek = () => {
    const prevDate = new Date(currentDate);
    prevDate.setDate(prevDate.getDate() - 7);
    setCurrentDate(prevDate);
  };

  // Hilfsfunktionen für Techniker-Arbeitstage
  const isTechnikerWorkDay = (dateString) => {
    if (!isTechniker()) return true; // Admin/Manager können alle Tage sehen
    return technikerWorkDays.some(workDay => workDay.date === dateString);
  };

  const getTechnikerWorkDayInfo = (dateString) => {
    if (!isTechniker()) return null;
    return technikerWorkDays.find(workDay => workDay.date === dateString);
  };

  const handleDayClick = async (day) => {
    console.log('🖱️ handleDayClick called:', {
      day: day,
      is_workday: day?.is_workday,
      date: day?.date
    });
    
    if (!day || !day.is_workday) {
      console.log('❌ Day click blocked:', { hasDay: !!day, is_workday: day?.is_workday });
      return;
    }
    
    // Handle both month view and week view day objects
    // Month view: day has 'day' property (day number)
    // Week view: day has 'date' property (Date object)
    let clickedDate;
    if (day.date instanceof Date) {
      // Week view - day.date is already a Date object
      clickedDate = day.date;
    } else {
      // Month view - construct Date from day number
      clickedDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day.day);
    }
    
    // Fix: Use local date formatting to avoid timezone offset
    const year = clickedDate.getFullYear();
    const month = String(clickedDate.getMonth() + 1).padStart(2, '0');
    const dayNum = String(clickedDate.getDate()).padStart(2, '0');
    const dateString = `${year}-${month}-${dayNum}`;
    
    console.log(`🖱️ Day clicked: ${clickedDate.toLocaleDateString('de-DE')} -> dateString: ${dateString}`);
    
    // Techniker: Open Billing Modal für ihre Arbeitstage
    if (isTechniker()) {
      if (!isTechnikerWorkDay(dateString)) {
        alert('Sie sind an diesem Tag keinem Team zugewiesen.');
        return;
      }
      handleOpenBilling(dateString);
      return;
    }
    
    // Admin/Manager: Open Day Details Modal
    setSelectedDay(clickedDate);
    setShowDayModal(true);
    
    await loadDayDetails(clickedDate);
  };

  const saveDailyOrders = async () => {
    try {
      const token = localStorage.getItem('authToken');
      // Fix: Use local date formatting to avoid timezone offset
      const year = selectedDay.getFullYear();
      const month = String(selectedDay.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDay.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;
      
      await axios.post(`${BACKEND_URL}/api/calendar/daily-assignment`, {
        assignment_date: dateString,
        connect_offtracks_total: dailyOrders.connect_offtracks_total,
        gwv_offtracks_total: dailyOrders.gwv_offtracks_total,
        notes: dailyOrders.notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert('Tagesaufträge erfolgreich gespeichert!');
      await loadCalendarMonth(); // Refresh calendar
      
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      alert('Fehler beim Speichern der Tagesaufträge');
    }
  };

  const assignTeam = async () => {
    try {
      const token = localStorage.getItem('authToken');
      // Fix: Use local date formatting to avoid timezone offset
      const year = selectedDay.getFullYear();
      const month = String(selectedDay.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDay.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;
      
      // Extract employee IDs from team (supports both old and new formats)
      let employee1_id = '';
      let employee2_id = '';
      
      // Priority 1: Use member_ids array (new format)
      if (selectedTeamForAssignment.member_ids && selectedTeamForAssignment.member_ids.length > 0) {
        employee1_id = selectedTeamForAssignment.member_ids[0] || '';
        employee2_id = selectedTeamForAssignment.member_ids[1] || '';
      }
      // Priority 2: Fallback to old format (member1_id/member2_id)
      else {
        employee1_id = selectedTeamForAssignment.member1_id || '';
        employee2_id = selectedTeamForAssignment.member2_id || '';
      }
      
      console.log('🔧 Team Assignment Debug:');
      console.log('  - Selected team:', selectedTeamForAssignment);
      console.log('  - Extracted employee1_id:', employee1_id);
      console.log('  - Extracted employee2_id:', employee2_id);
      
      // Use team's predefined members and order type
      const assignmentData = {
        assignment_date: dateString,
        team_number: selectedTeamForAssignment.team_number,
        employee1_id: employee1_id,
        employee2_id: employee2_id,
        order_type: selectedTeamForAssignment.order_type,
        connect_offtracks: teamAssignmentForm.connect_offtracks,
        gwv_offtracks: teamAssignmentForm.gwv_offtracks,
        notes: teamAssignmentForm.notes
      };
      
      console.log('  - Assignment data being sent:', assignmentData);

      await axios.post(`${BACKEND_URL}/api/calendar/team-assignment`, assignmentData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert('Team erfolgreich zugewiesen!');
      setSelectedTeamForAssignment(null);
      setTeamAssignmentForm({
        team_id: '',
        connect_offtracks: 0,
        gwv_offtracks: 0,
        notes: ''
      });
      
      await loadDayDetails(selectedDay);
      
    } catch (error) {
      console.error('Fehler beim Zuweisen:', error);
      alert('Fehler: ' + (error.response?.data?.detail || 'Unbekannter Fehler'));
    }
  };

  const deleteTeamAssignment = async (assignmentId) => {
    if (!window.confirm('Team-Zuweisung wirklich löschen?')) return;
    
    try {
      const token = localStorage.getItem('authToken');
      await axios.delete(`${BACKEND_URL}/api/calendar/team-assignment/${assignmentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert('Team-Zuweisung gelöscht!');
      await loadDayDetails(selectedDay);
      
    } catch (error) {
      console.error('Fehler beim Löschen:', error);
      alert('Fehler beim Löschen der Team-Zuweisung');
    }
  };

  // Helper functions
  const formatDate = (date) => {
    return new Intl.DateTimeFormat('de-DE', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isTomorrow = (date) => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return date.toDateString() === tomorrow.toDateString();
  };

  const handleDateNavigation = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + direction);
    setSelectedDate(newDate);
  };

  const handleToday = () => {
    setSelectedDate(new Date());
  };

  const handleTomorrow = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setSelectedDate(tomorrow);
  };

  const isEmployeeConflicted = (employeeId) => {
    if (!dayDetails?.assigned_employees) return false;
    return dayDetails.assigned_employees.includes(employeeId);
  };

  const getEmployeeConflictMessage = (employeeId) => {
    if (isEmployeeConflicted(employeeId)) {
      return "Mitarbeiter ist bereits einem anderen Team zugewiesen";
    }
    return "";
  };

  const getTeamMembers = (team) => {
    const teamMembers = [];
    if (team.member1_id) teamMembers.push(team.member1_id);
    if (team.member2_id) teamMembers.push(team.member2_id);
    if (team.member_ids) teamMembers.push(...team.member_ids);
    return teamMembers;
  };

  const isTeamAssigned = (team) => {
    if (!dayDetails?.team_assignments) return false;
    // Check if this team is already assigned for the day
    return dayDetails.team_assignments.some(assignment => 
      assignment.team_number === team.team_number
    );
  };

  const isTeamConflicted = (team) => {
    if (!dayDetails?.assigned_employees) return false;
    if (isTeamAssigned(team)) return false; // Don't mark assigned teams as conflicted
    
    // Check if any team member is already assigned to another team
    const teamMembers = getTeamMembers(team);
    return teamMembers.some(memberId => dayDetails.assigned_employees.includes(memberId));
  };

  const getConflictedMembers = (team) => {
    if (!dayDetails?.assigned_employees) return [];
    
    const teamMembers = getTeamMembers(team);
    return teamMembers.filter(memberId => dayDetails.assigned_employees.includes(memberId));
  };

  // Check if employee is on vacation or sick on a specific date
  const isEmployeeUnavailable = (employeeId, date) => {
    if (!vacations || vacations.length === 0) return null;
    
    const dateObj = new Date(date);
    dateObj.setHours(0, 0, 0, 0);
    
    const vacation = vacations.find(v => {
      if (v.employee_id !== employeeId) return false;
      
      const startDate = new Date(v.start_date);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(v.end_date);
      endDate.setHours(23, 59, 59, 999);
      
      return dateObj >= startDate && dateObj <= endDate;
    });
    
    return vacation;
  };

  // Get unavailable team members
  const getUnavailableTeamMembers = (team) => {
    if (!selectedDay || !team) return [];
    
    const teamMembers = getTeamMembers(team);
    const unavailableMembers = [];
    
    teamMembers.forEach(memberId => {
      const vacation = isEmployeeUnavailable(memberId, selectedDay);
      if (vacation) {
        const employee = employees.find(e => e.id === memberId);
        if (employee) {
          unavailableMembers.push({
            employee,
            vacation
          });
        }
      }
    });
    
    return unavailableMembers;
  };

  const getTeamStatus = (team) => {
    // Check for unavailable members first
    const unavailableMembers = getUnavailableTeamMembers(team);
    if (unavailableMembers.length > 0) {
      const isSick = unavailableMembers.some(m => m.vacation.vacation_type === 'krankheitstage');
      const isVacation = unavailableMembers.some(m => m.vacation.vacation_type === 'jahresurlaub' || m.vacation.vacation_type === 'sonderurlaub' || m.vacation.vacation_type === 'unbezahlt');
      
      let message = 'Mitarbeiter nicht verfügbar: ';
      message += unavailableMembers.map(m => {
        const type = m.vacation.vacation_type === 'krankheitstage' ? 'krank' : 'im Urlaub';
        return `${m.employee.first_name} ${m.employee.last_name} (${type})`;
      }).join(', ');
      
      return {
        status: 'unavailable',
        color: 'red',
        message: message,
        disabled: true,
        unavailableMembers
      };
    }
    
    if (isTeamAssigned(team)) {
      return {
        status: 'assigned',
        color: 'green',
        message: 'Zuweisung erfolgreich geplant',
        disabled: false
      };
    } else if (isTeamConflicted(team)) {
      return {
        status: 'conflicted',
        color: 'red', 
        message: 'Mitarbeiter bereits anderem Team zugewiesen',
        disabled: true
      };
    } else {
      return {
        status: 'available',
        color: 'default',
        message: 'Team verfügbar für Zuweisung',
        disabled: false
      };
    }
  };

  if (loading) {
    return (
      <div className="min-h-full bg-slate-50 p-6 flex items-center justify-center">
        <div className="text-center">
          <CalendarIcon className="w-12 h-12 text-emerald-600 mx-auto animate-pulse" />
          <p className="text-slate-600 mt-2">Lädt Kalender...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-slate-50 p-6" data-testid="calendar-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
              <CalendarIcon className="w-8 h-8 text-emerald-600" />
              Calendar Übersicht 📅
            </h1>
            <p className="text-slate-600 mt-1">
              Team-Zuweisung mit Mitarbeiter-Konflikterkennung für Connect- und GWV-Aufträge
            </p>
          </div>
          
          <div className="flex gap-2">
            {/* View Mode Selector */}
            <div className="flex bg-slate-100 rounded-lg p-1 mr-2">
              <Button
                variant={viewMode === 'week' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('week')}
                className="rounded-md"
              >
                Woche
              </Button>
              <Button
                variant={viewMode === 'month' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('month')}
                className="rounded-md"
              >
                Monat
              </Button>
            </div>
            
            {/* PDF Export Buttons */}
            {!isTechniker() && (
              <>
                <Button
                  variant="outline"
                  onClick={handleExportDayPDF}
                  className="gap-2"
                  title="Aktuellen Tag als PDF exportieren"
                >
                  <FileDown className="w-4 h-4" />
                  Tag als PDF
                </Button>
                <Button
                  variant="outline"
                  onClick={handleExportWeekPDF}
                  className="gap-2"
                  title="Aktuelle Woche als PDF exportieren"
                >
                  <FileDown className="w-4 h-4" />
                  Woche als PDF
                </Button>
              </>
            )}
            
            <Button
              variant="outline"
              onClick={viewMode === 'week' ? loadWeekData : loadCalendarMonth}
              className="gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Aktualisieren
            </Button>
          </div>
        </div>
      </div>

      {/* Calendar Navigation */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <Button variant="outline" onClick={viewMode === 'week' ? prevWeek : () => navigateMonth(-1)}>
              <ChevronLeft className="w-4 h-4" />
              {viewMode === 'week' ? 'Vorherige Woche' : 'Vorheriger Monat'}
            </Button>
            
            <CardTitle className="text-2xl">
              {viewMode === 'week' && weekData ? (
                `KW${weekData.weekNumber}: ${weekData.startDate.toLocaleDateString('de-DE', { 
                  day: '2-digit', 
                  month: '2-digit', 
                  year: 'numeric' 
                })} - ${weekData.endDate.toLocaleDateString('de-DE', { 
                  day: '2-digit', 
                  month: '2-digit', 
                  year: 'numeric' 
                })}`
              ) : (
                calendarData ? calendarData.month_name : 'Lädt...'
              )}
            </CardTitle>
            
            <Button variant="outline" onClick={viewMode === 'week' ? nextWeek : () => navigateMonth(1)}>
              {viewMode === 'week' ? 'Nächste Woche' : 'Nächster Monat'}
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Calendar Grid */}
      {viewMode === 'week' ? (
        weekData ? (
          <WeekView 
            weekData={weekData} 
            onDayClick={handleDayClick}
            isTechniker={isTechniker()}
            isTechnikerWorkDay={isTechnikerWorkDay}
            getTechnikerWorkDayInfo={getTechnikerWorkDayInfo}
          />
        ) : (
          <Card>
            <CardContent className="p-6">
              <div className="text-center py-12">
                <CalendarIcon className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Wochendaten werden geladen...</p>
              </div>
            </CardContent>
          </Card>
        )
      ) : (
        <Card>
          <CardContent className="p-6">
            {calendarData ? (
              <div className="space-y-4">
                {/* Info für Techniker */}
                {isTechniker() && (
                  <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2 mb-2">
                      <CalendarIcon className="w-5 h-5 text-blue-600" />
                      <span className="font-medium text-blue-900">Techniker-Kalender</span>
                    </div>
                    <p className="text-blue-700 text-sm">
                      Grün markierte Tage sind Ihre Arbeitstage. Klicken Sie darauf, um Details zu sehen.
                    </p>
                  </div>
                )}
                
                {/* Weekday Headers */}
                <div className="grid grid-cols-7 gap-2 mb-4">
                  {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map(day => (
                    <div key={day} className="p-2 text-center font-medium text-slate-600">
                      {day}
                    </div>
                  ))}
                </div>
                
                {/* Calendar Days */}
                {calendarData.calendar_weeks.map((week, weekIndex) => (
                  <div key={weekIndex} className="grid grid-cols-7 gap-2">
                    {week.map((day, dayIndex) => (
                      <CalendarDay
                        key={dayIndex}
                        day={day}
                        onClick={handleDayClick}
                        isTechniker={isTechniker()}
                        isTechnikerWorkDay={isTechnikerWorkDay}
                        getTechnikerWorkDayInfo={getTechnikerWorkDayInfo}
                        currentDate={currentDate}
                      />
                    ))}
                  </div>
                ))}
                
                {/* Month Summary */}
                <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                  <h3 className="font-medium mb-2">Monats-Zusammenfassung:</h3>
                  <div className="grid grid-cols-1 gap-4 text-sm">
                    <div>
                      <span className="text-slate-600">Arbeitstage: </span>
                      <span className="font-bold">{calendarData.summary.total_workdays}</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <CalendarIcon className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Kalender wird geladen...</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Date Navigation */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex items-center justify-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleDateNavigation(-1)}
              data-testid="previous-day-button"
            >
              <ChevronLeft className="w-4 h-4" />
              Zurück
            </Button>
            
            <div className="text-center">
              <h2 className="text-xl font-semibold text-slate-900">
                {formatDate(selectedDate)}
              </h2>
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleDateNavigation(1)}
              data-testid="next-day-button"
            >
              Vor
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Calendar Summary */}
      {calendarData && (
        <div className="grid grid-cols-1 gap-6 mb-6">
          <Card className="border-l-4 border-l-emerald-500">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600 mb-1">Arbeitstage</p>
                  <p className="text-3xl font-bold text-slate-900">
                    {calendarData.summary?.total_workdays || 0}
                  </p>
                </div>
                <CalendarIcon className="w-8 h-8 text-emerald-600" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Day Detail Modal */}
      {showDayModal && selectedDay && (
        <DayDetailModal
          selectedDay={selectedDay}
          dayDetails={dayDetails}
          dailyOrders={dailyOrders}
          setDailyOrders={setDailyOrders}
          teamAssignments={teamAssignments}
          availableTeams={availableTeams}
          selectedTeamForAssignment={selectedTeamForAssignment}
          setSelectedTeamForAssignment={setSelectedTeamForAssignment}
          teamAssignmentForm={teamAssignmentForm}
          setTeamAssignmentForm={setTeamAssignmentForm}
          employees={employees}
          onClose={() => setShowDayModal(false)}
          onSaveDailyOrders={saveDailyOrders}
          onAssignTeam={assignTeam}
          onDeleteTeam={deleteTeamAssignment}
          isEmployeeConflicted={isEmployeeConflicted}
          getEmployeeConflictMessage={getEmployeeConflictMessage}
          isTeamConflicted={isTeamConflicted}
          getConflictedMembers={getConflictedMembers}
          getTeamStatus={getTeamStatus}
        />
      )}

      {/* Billing Modal für Techniker */}
      {showBillingModal && selectedBillingDate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">
                    Abrechnung - {new Date(selectedBillingDate).toLocaleDateString('de-DE', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </h2>
                  <p className="text-slate-600 mt-1">
                    Team {getWorkDayInfo(selectedBillingDate)?.team_number} • 
                    {getWorkDayInfo(selectedBillingDate)?.team_members?.map(m => m.name).join(', ') || 'Keine Mitglieder'}
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowBillingModal(false);
                    setBillingData(null);
                    setBillingOrders([]);
                    setSupportTeams([]);
                  }}
                  disabled={billingData?.confirmed}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Supported Teams Selection */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-500" />
                    Unterstützung durch andere Teams (optional)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {availableTeams
                      .filter(team => team.team_number !== getWorkDayInfo(selectedBillingDate)?.team_number)
                      .map(team => (
                        <Button
                          key={team.team_number}
                          variant={supportTeams.includes(team.team_number) ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => {
                            if (supportTeams.includes(team.team_number)) {
                              setSupportTeams(supportTeams.filter(t => t !== team.team_number));
                            } else {
                              setSupportTeams([...supportTeams, team.team_number]);
                            }
                          }}
                          disabled={billingData?.confirmed}
                        >
                          Team {team.team_number}
                        </Button>
                      ))
                    }
                  </div>
                </CardContent>
              </Card>

              {/* Orders Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 justify-between">
                    <div className="flex items-center gap-2">
                      <CalendarIcon className="w-5 h-5 text-emerald-500" />
                      Aufträge
                    </div>
                    {!billingData?.confirmed && (
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => addBillingOrder('gwv')}>
                          + GWV Auftrag
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => addBillingOrder('connect')}>
                          + Connect Auftrag
                        </Button>
                      </div>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {billingOrders.length === 0 ? (
                    <p className="text-slate-500 text-center py-8">
                      Noch keine Aufträge hinzugefügt. Klicken Sie auf "+ GWV Auftrag" oder "+ Connect Auftrag" um zu beginnen.
                    </p>
                  ) : (
                    billingOrders.map((order, orderIndex) => (
                      <Card key={orderIndex} className="border-l-4 border-l-blue-500">
                        <CardContent className="pt-4">
                          <div className="flex items-center justify-between mb-4">
                            <h3 className="font-semibold text-lg">
                              {order.order_type === 'gwv' ? 'GWV' : 'Connect'} {order.order_number}
                            </h3>
                            {!billingData?.confirmed && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeBillingOrder(orderIndex)}
                              >
                                <X className="w-4 h-4 text-red-500" />
                              </Button>
                            )}
                          </div>

                          {/* SMILE Number */}
                          <div className="mb-4">
                            <label className="block text-sm font-medium mb-2">SMILE-Nummer *</label>
                            <Input
                              value={order.smile_number}
                              onChange={(e) => updateBillingOrder(orderIndex, 'smile_number', e.target.value)}
                              placeholder="z.B. SM-2025-001"
                              disabled={billingData?.confirmed}
                              required
                            />
                          </div>

                          {/* Positions */}
                          <div className="space-y-3">
                            <label className="block text-sm font-medium">Positionen</label>
                            
                            {order.positions.map((position, positionIndex) => (
                              <div key={positionIndex} className="flex gap-2 items-center bg-slate-50 p-3 rounded">
                                <div className="flex-1">
                                  <p className="font-medium text-sm">
                                    {position.position_number} - {position.description}
                                  </p>
                                  <p className="text-xs text-slate-500">
                                    {position.unit_price.toFixed(2)} € / {position.unit}
                                  </p>
                                </div>
                                <div className="w-24">
                                  <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={position.quantity}
                                    onChange={(e) => updatePositionQuantity(orderIndex, positionIndex, e.target.value)}
                                    disabled={billingData?.confirmed}
                                    className="text-right"
                                  />
                                </div>
                                <div className="w-28 text-right font-medium">
                                  {position.subtotal.toFixed(2)} €
                                </div>
                                {!billingData?.confirmed && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => removePositionFromOrder(orderIndex, positionIndex)}
                                  >
                                    <X className="w-4 h-4 text-red-500" />
                                  </Button>
                                )}
                              </div>
                            ))}

                            {!billingData?.confirmed && (
                              <div className="flex gap-2">
                                <select
                                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                  onChange={(e) => {
                                    if (e.target.value) {
                                      addPositionToOrder(orderIndex, e.target.value);
                                      e.target.value = '';
                                    }
                                  }}
                                >
                                  <option value="">Position hinzufügen...</option>
                                  {billingPositions.map(pos => (
                                    <option key={pos.id} value={pos.id}>
                                      {pos.position_number} - {pos.description} ({pos.unit_price.toFixed(2)} € / {pos.unit})
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}
                          </div>

                          {/* Order Total */}
                          <div className="mt-4 pt-4 border-t flex justify-between items-center">
                            <span className="font-medium">Auftragssumme:</span>
                            <span className="text-xl font-bold text-emerald-600">
                              {order.total.toFixed(2)} €
                            </span>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}

                  {/* Total Amount */}
                  {billingOrders.length > 0 && (
                    <div className="bg-slate-100 p-4 rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="text-lg font-semibold">Gesamtsumme:</span>
                        <span className="text-2xl font-bold text-emerald-600">
                          {calculateTotalAmount().toFixed(2)} €
                        </span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Removed Confirmation Checkbox - direkt an Buchhaltung senden */}

              {billingData?.confirmed && (
                <Card className="border-green-500 bg-green-50">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 text-green-700">
                      <CheckCircle className="w-5 h-5" />
                      <span className="font-medium">
                        Abrechnung wurde bestätigt am {new Date(billingData.confirmed_at).toLocaleString('de-DE')}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 justify-end">
                {billingOrders.length > 0 && (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowBillingModal(false);
                        setBillingData(null);
                        setBillingOrders([]);
                        setSupportTeams([]);
                      }}
                    >
                      <X className="w-4 h-4 mr-2" />
                      Abbrechen
                    </Button>
                    <Button
                      variant="default"
                      onClick={() => saveBilling(false)}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Speichern und an Buchhaltung senden
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Removed unused components to fix JavaScript errors
// Team Assignment Card Component (removed)
const TeamAssignmentCard_REMOVED = ({ assignment, onDelete, getStatusColor, getStatusIcon }) => {
  return (
    <div className="p-4 border border-slate-200 rounded-lg bg-white hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-3">
            <h3 className="font-semibold text-lg">Team {assignment.team_number || 'Unbekannt'}</h3>
            <Badge className={`${getStatusColor(assignment.status || 'verfügbar')} border gap-1`}>
              {getStatusIcon(assignment.status || 'verfügbar')}
              {assignment.status || 'verfügbar'}
            </Badge>
            <Badge variant="outline" className="uppercase font-medium">
              {assignment.order_type || 'Unbekannt'}
            </Badge>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
            <div>
              <h4 className="font-medium text-slate-700 mb-2">Mitarbeiter:</h4>
              <div className="space-y-1">
                {Array.isArray(assignment.employees) && assignment.employees.length > 0 ? (
                  assignment.employees.map((emp) => (
                    <div key={emp.id} className="flex items-center gap-2">
                      <span className="text-sm">{emp.name || 'Unbekannt'}</span>
                      <Badge className={`text-xs ${getStatusColor(emp.status || 'verfügbar')} border`}>
                        {emp.status || 'verfügbar'}
                      </Badge>
                    </div>
                  ))
                ) : (
                  <div className="text-sm text-slate-500">Keine Mitarbeiter zugewiesen</div>
                )}
              </div>
            </div>
            
            <div>
              <h4 className="font-medium text-slate-700 mb-2">Aufträge:</h4>
              <div className="space-y-1">
                {(assignment.order_count_connect || 0) > 0 && (
                  <div className="text-sm">Connect: {assignment.order_count_connect || 0}</div>
                )}
                {(assignment.order_count_gwv || 0) > 0 && (
                  <div className="text-sm">GWV: {assignment.order_count_gwv || 0}</div>
                )}
                {(assignment.order_count_connect || 0) === 0 && (assignment.order_count_gwv || 0) === 0 && (
                  <div className="text-sm text-slate-500">Keine Aufträge zugewiesen</div>
                )}
              </div>
            </div>
          </div>
          
          {assignment.notes && typeof assignment.notes === 'string' && assignment.notes.trim() && (
            <div className="text-sm text-slate-600 bg-slate-50 p-2 rounded">
              <strong>Notizen:</strong> {assignment.notes}
            </div>
          )}
        </div>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(assignment.assignment_id)}
          className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50"
          data-testid={`delete-assignment-${assignment.assignment_id}`}
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

// Assignment Form Component (removed)
const AssignmentForm_REMOVED = ({ formData, setFormData, onSubmit, submitting, teams, selectedDate, onClose }) => {
  return (
    <DialogContent className="sm:max-w-md" aria-describedby="assignment-form-description">
      <DialogHeader>
        <DialogTitle>
          Team zuweisen für {selectedDate.toLocaleDateString('de-DE')}
        </DialogTitle>
        <p id="assignment-form-description" className="text-sm text-slate-600">
          Wählen Sie ein Team und die Auftragsdetails für den ausgewählten Tag.
        </p>
      </DialogHeader>
      
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">Team</label>
          <Select value={formData.team_id} onValueChange={(value) => setFormData({ ...formData, team_id: value })}>
            <SelectTrigger data-testid="assignment-team-select">
              <SelectValue placeholder="Team auswählen..." />
            </SelectTrigger>
            <SelectContent>
              {teams.map(team => (
                <SelectItem key={team.id} value={team.id}>
                  Team {team.team_number} ({team.order_type})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Auftragsart</label>
          <Select value={formData.order_type} onValueChange={(value) => setFormData({ ...formData, order_type: value })}>
            <SelectTrigger data-testid="assignment-order-type-select">
              <SelectValue placeholder="Auftragsart wählen..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="connect">Connect</SelectItem>
              <SelectItem value="gwv">GWV</SelectItem>
              <SelectItem value="beides">Beides</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Connect Aufträge</label>
            <Input
              type="number"
              value={formData.order_count_connect}
              onChange={(e) => setFormData({ ...formData, order_count_connect: parseInt(e.target.value) || 0 })}
              min="0"
              data-testid="connect-orders-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">GWV Aufträge</label>
            <Input
              type="number"
              value={formData.order_count_gwv}
              onChange={(e) => setFormData({ ...formData, order_count_gwv: parseInt(e.target.value) || 0 })}
              min="0"
              data-testid="gwv-orders-input"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Notizen (optional)</label>
          <Input
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="Zusätzliche Informationen..."
            data-testid="assignment-notes-input"
          />
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button 
            type="button" 
            variant="outline" 
            disabled={submitting}
            onClick={onClose}
          >
            Abbrechen
          </Button>
          <Button type="submit" disabled={submitting} data-testid="save-assignment-button">
            {submitting ? (
              <>
                <div className="loading-spinner mr-2"></div>
                Zuweisen...
              </>
            ) : (
              'Team zuweisen'
            )}
          </Button>
        </div>
      </form>
    </DialogContent>
  );
};

// Week View Component
const WeekView = ({ weekData, onDayClick, isTechniker, isTechnikerWorkDay, getTechnikerWorkDayInfo }) => {
  if (!weekData) return <div>Laden...</div>;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-slate-200">
      {/* Info für Techniker auch in Wochenansicht */}
      {isTechniker && (
        <div className="m-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-center gap-2 mb-2">
            <CalendarIcon className="w-5 h-5 text-blue-600" />
            <span className="font-medium text-blue-900">Techniker-Kalender</span>
          </div>
          <p className="text-blue-700 text-sm">
            Grün markierte Tage sind Ihre Arbeitstage. Klicken Sie darauf, um Ihre Abrechnung zu erfassen.
          </p>
        </div>
      )}
      
      {/* Week Header */}
      <div className="p-4 border-b border-slate-200 bg-slate-50">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-800">
            KW{weekData.weekNumber}: {weekData.startDate.toLocaleDateString('de-DE', { 
              day: '2-digit', 
              month: '2-digit', 
              year: 'numeric' 
            })} bis {weekData.endDate.toLocaleDateString('de-DE', { 
              day: '2-digit', 
              month: '2-digit', 
              year: 'numeric' 
            })}
          </h3>
          <Badge variant="outline" className="font-medium">
            Wochenansicht
          </Badge>
        </div>
      </div>

      {/* Week Days Vertical Layout */}
      <div className="p-4">
        <div className="space-y-4">
          {/* Day Cells - Vertical Layout */}
          {weekData.days.map((day, index) => {
            // Verwende das tatsächliche Datum des Tages, nicht den Array-Index
            const dayName = day.date.toLocaleDateString('de-DE', { weekday: 'long' });
            const isToday = new Date(day.date).toDateString() === new Date().toDateString();
            const hasTeams = day.team_assignments && day.team_assignments.length > 0;
            
            // Check if day is a German public holiday
            const isHoliday = isGermanPublicHoliday(day.date);
            const holidayName = isHoliday ? getHolidayName(day.date) : null;
            
            // Techniker-spezifische Styling-Logik
            // Fix: Use local date formatting to avoid timezone offset
            const year = day.date.getFullYear();
            const month = String(day.date.getMonth() + 1).padStart(2, '0');
            const dayNum = String(day.date.getDate()).padStart(2, '0');
            const dateString = `${year}-${month}-${dayNum}`;
            const isOwnWorkDay = isTechniker ? isTechnikerWorkDay(dateString) : hasTeams;
            const techWorkInfo = isTechniker ? getTechnikerWorkDayInfo(dateString) : null;
            const isClickable = !isHoliday && (isTechniker ? (day.is_workday && isOwnWorkDay) : day.is_workday);
            
            // Neue Logik für Farbmarkierung
            const hasDailyOrders = day.daily_assignment && 
                                   (day.daily_assignment.connect_offtracks_total > 0 || 
                                    day.daily_assignment.gwv_offtracks_total > 0);
            // hasTeams already defined above in line 1812
            const isCompleted = hasDailyOrders && hasTeams && 
                               day.daily_assignment.connect_remaining === 0 && 
                               day.daily_assignment.gwv_remaining === 0;
            const needsTeams = hasDailyOrders && !hasTeams;
            
            // Ist es ein Wochenende oder Feiertag?
            const isWeekendOrHoliday = !day.is_workday || isHoliday;
            
            // WeekView rendering - simplified without order tracking
            console.log(`🎨 WeekView Render Index ${index}: ${day.date.toLocaleDateString('de-DE')} (${dayName}) - Teams: ${day.team_assignments?.length || 0} - Holiday: ${isHoliday} - is_workday: ${day.is_workday} - isClickable: ${isClickable}`);
            
            return (
              <div
                key={`${year}-${month}-${dayNum}`}
                className={`
                  w-full p-4 border-2 rounded-lg transition-all
                  ${isWeekendOrHoliday ? 'bg-[#F5E8D0] border-[#E5D8C0] cursor-not-allowed' : ''}
                  ${!isWeekendOrHoliday && isCompleted ? 'bg-[#D0F0C0] border-[#B0D0A0]' : ''}
                  ${!isWeekendOrHoliday && needsTeams && !isCompleted ? 'bg-[#FFF4CC] border-[#FFE999]' : ''}
                  ${!isWeekendOrHoliday && !hasDailyOrders && isClickable ? 'cursor-pointer hover:bg-slate-50 border-slate-200 hover:border-slate-300' : ''}
                  ${!isWeekendOrHoliday && !isClickable && !isWeekendOrHoliday ? 'bg-slate-100 border-slate-300 cursor-not-allowed' : ''}
                  ${isToday && !isWeekendOrHoliday ? 'ring-2 ring-emerald-500' : ''}
                  ${!isWeekendOrHoliday && isTechniker && isOwnWorkDay && day.is_workday && hasDailyOrders && !isCompleted && !needsTeams ? 'bg-slate-50 border-slate-200' : ''}
                `}
                onClick={() => !isWeekendOrHoliday && isClickable && onDayClick(day)}
              >
                {/* Day Header with Name and Date */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <h4 className={`text-lg font-semibold ${isHoliday ? 'text-red-700' : index < 5 ? 'text-slate-700' : 'text-slate-400'}`}>
                      {dayName}
                    </h4>
                    <span className={`text-2xl font-bold ${isHoliday ? 'text-red-700' : isToday ? 'text-emerald-600' : day.is_workday ? 'text-slate-900' : 'text-slate-400'}`}>
                      {day.date.getDate()}.{String(day.date.getMonth() + 1).padStart(2, '0')}.
                    </span>
                    {/* Date Verification Debug Display */}
                    <span className="text-xs text-gray-400 ml-2">
                      ({day.date.toLocaleDateString('de-DE')})
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {isHoliday && (
                      <Badge className="bg-red-500 text-white">
                        Feiertag
                      </Badge>
                    )}
                    {isToday && !isHoliday && (
                      <Badge className="bg-emerald-500">Heute</Badge>
                    )}
                    {!isHoliday && isTechniker && isOwnWorkDay && day.is_workday && (
                      <Badge className="bg-green-500 text-white text-xs">Arbeitstag</Badge>
                    )}
                    {!isHoliday && !day.is_workday && (
                      <Badge variant="secondary" className="text-xs">
                        Wochenende
                      </Badge>
                    )}
                  </div>
                </div>

                {isHoliday && (
                  <div className="text-sm text-red-700 font-medium">
                    {holidayName}
                  </div>
                )}

                {/* Team Assignments Only */}
                {!isHoliday && day.is_workday && (
                  <div className="space-y-2">
                    <h5 className="text-sm font-medium text-slate-600">Teams:</h5>
                    {day.team_assignments && day.team_assignments.length > 0 ? (
                      <div className="space-y-1">
                        {day.team_assignments.map((team, teamIndex) => (
                          <div key={teamIndex} className="p-2 bg-slate-50 rounded text-xs">
                            <div className="font-medium">{team.team_name || `Team ${team.team_number || teamIndex + 1}`}</div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-xs text-slate-400 text-center py-2">
                        Keine Teams zugewiesen
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Calendar Day Component (for month view)
const CalendarDay = ({ day, onClick, isTechniker, isTechnikerWorkDay, getTechnikerWorkDayInfo, currentDate }) => {
  if (!day) {
    return <div className="p-4"></div>; // Empty cell for non-month days
  }

  const isToday = () => {
    const today = new Date();
    return new Date(day.date).toDateString() === today.toDateString();
  };

  const hasTeams = day.team_assignments && day.team_assignments.length > 0;
  
  // Check if day is a German public holiday
  const dayDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day.day);
  const isHoliday = isGermanPublicHoliday(dayDate);
  const holidayName = isHoliday ? getHolidayName(dayDate) : null;
  
  // Techniker-spezifische Logik
  // Fix: Use local date formatting to avoid timezone offset
  const year = dayDate.getFullYear();
  const month = String(dayDate.getMonth() + 1).padStart(2, '0');
  const dayNum = String(dayDate.getDate()).padStart(2, '0');
  const dateString = `${year}-${month}-${dayNum}`;
  const isOwnWorkDay = isTechniker ? isTechnikerWorkDay(dateString) : hasTeams;
  const techWorkInfo = isTechniker ? getTechnikerWorkDayInfo(dateString) : null;
  const isClickable = isTechniker ? (day.is_workday && isOwnWorkDay) : day.is_workday;

  // Neue Logik für Farbmarkierung
  const hasDailyOrders = day.daily_assignment && 
                         (day.daily_assignment.connect_offtracks_total > 0 || 
                          day.daily_assignment.gwv_offtracks_total > 0);
  const hasTeamsAssigned = day.team_assignments && day.team_assignments.length > 0;
  const isCompleted = hasDailyOrders && hasTeamsAssigned && 
                     day.daily_assignment.connect_remaining === 0 && 
                     day.daily_assignment.gwv_remaining === 0;
  const needsTeams = hasDailyOrders && !hasTeamsAssigned;
  
  // Ist es ein Wochenende oder Feiertag?
  const isWeekendOrHoliday = !day.is_workday || isHoliday;

  return (
    <div
      className={`
        p-3 border rounded-lg transition-all min-h-[80px]
        ${isWeekendOrHoliday ? 'bg-[#F5E8D0] border-[#E5D8C0] cursor-not-allowed' : ''}
        ${!isWeekendOrHoliday && isCompleted ? 'bg-[#D0F0C0] border-[#B0D0A0]' : ''}
        ${!isWeekendOrHoliday && needsTeams && !isCompleted ? 'bg-[#FFF4CC] border-[#FFE999]' : ''}
        ${!isWeekendOrHoliday && !hasDailyOrders && isClickable ? 'cursor-pointer hover:bg-slate-50 border-slate-200' : ''}
        ${!isWeekendOrHoliday && !isClickable && !isWeekendOrHoliday ? 'bg-slate-100 border-slate-300 cursor-not-allowed' : ''}
        ${isToday() && !isWeekendOrHoliday ? 'ring-2 ring-emerald-500' : ''}
        ${!isWeekendOrHoliday && isTechniker && isOwnWorkDay && day.is_workday && hasDailyOrders && !isCompleted && !needsTeams ? 'bg-slate-50 border-slate-200' : ''}
      `}
      onClick={() => !isWeekendOrHoliday && isClickable && onClick(day)}
    >
      <div className="flex items-center justify-between mb-1">
        <span className={`font-bold ${isToday() ? 'text-emerald-600' : isHoliday ? 'text-red-700' : 'text-slate-900'}`}>
          {day.day}
        </span>
        <div className="flex flex-col gap-1">
          {isHoliday && (
            <Badge className="bg-red-500 text-white text-xs">Feiertag</Badge>
          )}
          {!isHoliday && isTechniker && isOwnWorkDay && day.is_workday && (
            <Badge className="bg-green-500 text-white text-xs">✓</Badge>
          )}
          {!isHoliday && !day.is_workday && (
            <Badge variant="secondary" className="text-xs">
              {new Date(day.date).getDay() === 0 ? 'So' : 'Sa'}
            </Badge>
          )}
        </div>
      </div>

      {isHoliday && (
        <div className="text-xs text-red-700 mt-1 font-medium">
          {holidayName}
        </div>
      )}

      {!isHoliday && day.is_workday && (
        <div className="text-xs text-slate-600 mt-1">
          {isTechniker && techWorkInfo ? (
            <div className="space-y-1">
              <div className="font-medium">Team {techWorkInfo.team_number}</div>
              {techWorkInfo.order_type && (
                <div className="text-xs opacity-75">{techWorkInfo.order_type}</div>
              )}
            </div>
          ) : (
            hasTeams && (
              <div>{day.team_assignments.length} Team{day.team_assignments.length !== 1 ? 's' : ''}</div>
            )
          )}
        </div>
      )}
    </div>
  );
};

// Day Detail Modal Component
const DayDetailModal = ({
  selectedDay,
  dayDetails,
  dailyOrders,
  setDailyOrders,
  teamAssignments,
  availableTeams,
  selectedTeamForAssignment,
  setSelectedTeamForAssignment,
  teamAssignmentForm,
  setTeamAssignmentForm,
  employees,
  onClose,
  onSaveDailyOrders,
  onAssignTeam,
  onDeleteTeam,
  isEmployeeConflicted,
  getEmployeeConflictMessage,
  isTeamConflicted,
  getConflictedMembers,
  getTeamStatus
}) => {
  if (!selectedDay || !dayDetails) return null;

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('de-DE', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-slate-900">
                {formatDate(selectedDay)}
              </h2>
              <p className="text-slate-600">{dayDetails.is_workday ? 'Arbeitstag' : 'Wochenende/Feiertag'}</p>
            </div>
            <Button variant="outline" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Daily Orders Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-500" />
                Tagesaufträge definieren
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Anzahl Connect-Aufträge
                  </label>
                  <Input
                    type="number"
                    min="0"
                    value={dailyOrders.connect_offtracks_total}
                    onChange={(e) => setDailyOrders(prev => ({
                      ...prev,
                      connect_offtracks_total: parseInt(e.target.value) || 0
                    }))}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Anzahl GWV-Aufträge
                  </label>
                  <Input
                    type="number"
                    min="0"
                    value={dailyOrders.gwv_offtracks_total}
                    onChange={(e) => setDailyOrders(prev => ({
                      ...prev,
                      gwv_offtracks_total: parseInt(e.target.value) || 0
                    }))}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Notizen</label>
                <Input
                  value={dailyOrders.notes}
                  onChange={(e) => setDailyOrders(prev => ({
                    ...prev,
                    notes: e.target.value
                  }))}
                  placeholder="Besondere Hinweise für diesen Tag..."
                />
              </div>

              <Button onClick={onSaveDailyOrders} className="w-full">
                <Save className="w-4 h-4 mr-2" />
                Tagesaufträge speichern
              </Button>

              {/* Remaining Orders Display */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div className="text-center">
                  <p className="text-sm text-slate-600">Connect verbleibend:</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {dayDetails?.daily_assignment?.connect_remaining || 0}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-slate-600">GWV verbleibend:</p>
                  <p className="text-2xl font-bold text-green-600">
                    {dayDetails?.daily_assignment?.gwv_remaining || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Team Assignments Section */}
          <Card>
            <CardHeader>
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-slate-800 mb-2">Vorhandene Teams zuweisen</h3>
                <p className="text-sm text-slate-600">
                  Wählen Sie ein bereits erstelltes Team aus und weisen Sie Mitarbeiter für diesen Tag zu. 
                  Neue Teams können im Teams-Modul erstellt werden.
                </p>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              
              {/* Available Teams Selection */}
              <Card className="border-blue-200 bg-blue-50">
                <CardContent className="p-4">
                  <h4 className="font-semibold mb-4 text-blue-800">
                    Team-Zuweisung für {selectedDay ? new Date(selectedDay).toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' }) : ''}
                  </h4>
                  
                  {availableTeams.length === 0 ? (
                    <div className="text-center py-8">
                      <Users className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                      <p className="text-slate-600 mb-2">Keine Teams verfügbar</p>
                      <p className="text-sm text-slate-500">
                        Erstellen Sie zuerst Teams im Teams-Modul, bevor Sie sie hier zuweisen können.
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Team auswählen</label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-96 overflow-y-auto">
                          {availableTeams.map(team => {
                            // Get team member names for display
                            const memberNames = [];
                            if (team.member1_id) {
                              const member1 = employees.find(emp => emp.id === team.member1_id);
                              if (member1) memberNames.push(`${member1.first_name} ${member1.last_name}`);
                            }
                            if (team.member2_id) {
                              const member2 = employees.find(emp => emp.id === team.member2_id);
                              if (member2) memberNames.push(`${member2.first_name} ${member2.last_name}`);
                            }
                            // Add member_ids members
                            if (team.member_ids) {
                              team.member_ids.forEach(memberId => {
                                const member = employees.find(emp => emp.id === memberId);
                                if (member && !memberNames.some(name => name === `${member.first_name} ${member.last_name}`)) {
                                  memberNames.push(`${member.first_name} ${member.last_name}`);
                                }
                              });
                            }

                            const teamStatus = getTeamStatus(team);
                            const isSelected = teamAssignmentForm.team_id === team.id;
                            
                            return (
                              <div
                                key={team.id}
                                onClick={() => {
                                  console.log('🖱️ Team card clicked:', team.team_number);
                                  console.log('🔍 Team status:', teamStatus);
                                  
                                  if (!teamStatus.disabled) {
                                    console.log('✅ Team is not disabled, selecting...');
                                    console.log('Selected team:', team);
                                    console.log('Available employees:', employees);
                                    console.log('Team member1_id:', team?.member1_id);
                                    console.log('Team member2_id:', team?.member2_id);
                                    console.log('Team member_ids:', team?.member_ids);
                                    setSelectedTeamForAssignment(team);
                                    setTeamAssignmentForm(prev => ({
                                      ...prev,
                                      team_id: team.id
                                    }));
                                  } else {
                                    console.log('❌ Team is disabled, cannot select');
                                  }
                                }}
                                className={`
                                  p-3 rounded-lg border-2 cursor-pointer transition-all
                                  ${isSelected ? 'border-blue-500 shadow-md' : 'border-slate-200'}
                                  ${teamStatus.disabled ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md'}
                                  ${teamStatus.color === 'green' ? 'bg-green-50 border-green-300' : ''}
                                  ${teamStatus.color === 'red' ? 'bg-red-50 border-red-300' : ''}
                                  ${teamStatus.color === 'default' ? 'bg-white' : ''}
                                `}
                                title={teamStatus.message}
                              >
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <Users className={`w-4 h-4 ${
                                      teamStatus.color === 'green' ? 'text-green-600' :
                                      teamStatus.color === 'red' ? 'text-red-600' :
                                      'text-blue-600'
                                    }`} />
                                    <span className={`font-bold text-lg ${
                                      teamStatus.color === 'green' ? 'text-green-700' :
                                      teamStatus.color === 'red' ? 'text-red-700' :
                                      'text-slate-800'
                                    }`}>
                                      Team {team.team_number}
                                    </span>
                                  </div>
                                  {teamStatus.status === 'assigned' && (
                                    <span className="px-2 py-1 bg-green-200 text-green-800 text-xs font-semibold rounded">
                                      ✅ GEPLANT
                                    </span>
                                  )}
                                  {teamStatus.status === 'conflicted' && (
                                    <span className="px-2 py-1 bg-red-200 text-red-800 text-xs font-semibold rounded">
                                      ❌ BLOCKIERT
                                    </span>
                                  )}
                                  {teamStatus.status === 'unavailable' && (
                                    <span className="px-2 py-1 bg-red-200 text-red-800 text-xs font-semibold rounded">
                                      ⚠️ NICHT VERFÜGBAR
                                    </span>
                                  )}
                                </div>
                                <div className="text-sm text-slate-600 mb-1">
                                  <span className="font-medium">Auftragsart:</span> {team.order_type.toUpperCase()}
                                </div>
                                <div className="text-sm text-slate-700">
                                  <span className="font-medium">Mitarbeiter:</span>{' '}
                                  {memberNames.length > 0 ? memberNames.join(', ') : 'Keine Mitglieder'}
                                </div>
                                
                                {/* Unavailability Warning */}
                                {teamStatus.status === 'unavailable' && teamStatus.unavailableMembers && (
                                  <div className="mt-2 p-2 bg-red-100 border border-red-300 rounded">
                                    <div className="flex items-start gap-2">
                                      <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                                      <div className="text-xs text-red-800">
                                        {teamStatus.unavailableMembers.map((member, idx) => {
                                          const isSick = member.vacation.vacation_type === 'krankheitstage';
                                          const statusText = isSick ? 'ist krankgemeldet' : 'ist im Urlaub';
                                          return (
                                            <div key={idx} className="mb-1">
                                              <span className="font-semibold">{member.employee.first_name} {member.employee.last_name}</span> {statusText}
                                              {' '}({new Date(member.vacation.start_date).toLocaleDateString('de-DE')} - {new Date(member.vacation.end_date).toLocaleDateString('de-DE')})
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  </div>
                                )}
                                
                                {isSelected && (
                                  <div className="mt-2 pt-2 border-t border-blue-200">
                                    <span className="text-xs text-blue-700 font-medium">
                                      ← Ausgewählt für Zuweisung
                                    </span>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Team Overview Section removed as requested */}

                      {selectedTeamForAssignment && (
                        <>
                          <div className="col-span-full">
                            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                              <div className="flex items-center gap-2 mb-3">
                                <Users className="w-5 h-5 text-blue-600" />
                                <h5 className="font-semibold text-blue-800">Ausgewähltes Team für Zuweisung</h5>
                              </div>
                              
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <p className="text-lg font-bold text-blue-700 mb-1">
                                    Team {selectedTeamForAssignment.team_number}
                                  </p>
                                  <p className="text-sm text-blue-600 mb-3">
                                    Auftragsart: <span className="font-semibold">{selectedTeamForAssignment.order_type.toUpperCase()}</span>
                                  </p>
                                </div>
                                
                                <div className="space-y-2">
                                  {(() => {
                                    // Debug: Log employees array state
                                    console.log('🔍 Employee Resolution Debug:');
                                    console.log('  - Employees array length:', employees.length);
                                    console.log('  - Employees array:', employees);
                                    console.log('  - Selected team:', selectedTeamForAssignment);
                                    
                                    // Collect all member IDs (supports both old and new format)
                                    const memberIds = [];
                                    if (selectedTeamForAssignment.member1_id) {
                                      memberIds.push(selectedTeamForAssignment.member1_id);
                                    }
                                    if (selectedTeamForAssignment.member2_id) {
                                      memberIds.push(selectedTeamForAssignment.member2_id);
                                    }
                                    if (selectedTeamForAssignment.member_ids && Array.isArray(selectedTeamForAssignment.member_ids)) {
                                      memberIds.push(...selectedTeamForAssignment.member_ids);
                                    }
                                    
                                    console.log('  - Collected member IDs:', memberIds);
                                    
                                    // Remove duplicates
                                    const uniqueMemberIds = [...new Set(memberIds)];
                                    console.log('  - Unique member IDs:', uniqueMemberIds);
                                    
                                    // Check if employees array is loaded
                                    if (employees.length === 0) {
                                      console.warn('⚠️ Employees array is empty - attempting to display member IDs');
                                      if (uniqueMemberIds.length === 0) {
                                        return (
                                          <div className="flex items-center gap-2">
                                            <User className="w-4 h-4 text-slate-400" />
                                            <span className="text-sm text-slate-500">
                                              Keine Mitarbeiter zugewiesen
                                            </span>
                                          </div>
                                        );
                                      }
                                      
                                      // Fallback: Show member IDs when employee data not loaded
                                      return (
                                        <>
                                          {uniqueMemberIds.map((id, index) => (
                                            <div key={id} className="flex items-center gap-2">
                                              <User className="w-4 h-4 text-amber-600" />
                                              <span className="text-sm font-medium text-amber-800">
                                                Mitarbeiter {index + 1}: ID {id.slice(0, 8)}... (Lade Daten...)
                                              </span>
                                            </div>
                                          ))}
                                        </>
                                      );
                                    }
                                    
                                    // Find employee objects
                                    const teamMembers = uniqueMemberIds
                                      .map(id => {
                                        const emp = employees.find(e => e.id === id);
                                        console.log(`  - Looking for employee with ID ${id}:`, emp ? `Found: ${emp.first_name} ${emp.last_name}` : 'NOT FOUND');
                                        return emp;
                                      })
                                      .filter(emp => emp !== undefined);
                                    
                                    console.log('  - Resolved team members:', teamMembers);
                                    
                                    // Display members
                                    if (teamMembers.length === 0 && uniqueMemberIds.length > 0) {
                                      // IDs exist but no matching employees found - show IDs as fallback
                                      return (
                                        <>
                                          {uniqueMemberIds.map((id, index) => (
                                            <div key={id} className="flex items-center gap-2">
                                              <User className="w-4 h-4 text-red-600" />
                                              <span className="text-sm font-medium text-red-800">
                                                Mitarbeiter {index + 1}: ID {id.slice(0, 8)}... (Nicht gefunden)
                                              </span>
                                            </div>
                                          ))}
                                        </>
                                      );
                                    }
                                    
                                    if (teamMembers.length === 0) {
                                      return (
                                        <div className="flex items-center gap-2">
                                          <User className="w-4 h-4 text-slate-400" />
                                          <span className="text-sm text-slate-500">
                                            Keine Mitarbeiter zugewiesen
                                          </span>
                                        </div>
                                      );
                                    }
                                    
                                    return (
                                      <>
                                        {teamMembers.map((member, index) => (
                                          <div key={member.id} className="flex items-center gap-2">
                                            <User className="w-4 h-4 text-blue-600" />
                                            <span className="text-sm font-medium text-blue-800">
                                              Mitarbeiter {index + 1}: {member.first_name} {member.last_name}
                                            </span>
                                          </div>
                                        ))}
                                      </>
                                    );
                                  })()}
                                </div>
                              </div>
                              
                              <div className="mt-3 p-2 bg-blue-100 rounded text-xs text-blue-700">
                                <strong>📋 Diese Mitarbeiter werden automatisch dem ausgewählten Team für den Tag zugewiesen.</strong>
                              </div>
                            </div>
                          </div>

                          {/* Check for conflicts with existing team assignments */}
                          {(() => {
                            if (!selectedTeamForAssignment || !teamAssignments || teamAssignments.length === 0) {
                              return null;
                            }

                            // Collect member IDs of selected team
                            const selectedTeamMemberIds = [];
                            if (selectedTeamForAssignment.member1_id) {
                              selectedTeamMemberIds.push(selectedTeamForAssignment.member1_id);
                            }
                            if (selectedTeamForAssignment.member2_id) {
                              selectedTeamMemberIds.push(selectedTeamForAssignment.member2_id);
                            }
                            if (selectedTeamForAssignment.member_ids && Array.isArray(selectedTeamForAssignment.member_ids)) {
                              selectedTeamMemberIds.push(...selectedTeamForAssignment.member_ids);
                            }
                            const uniqueSelectedMembers = [...new Set(selectedTeamMemberIds)];

                            // Check for conflicts with existing assignments
                            const conflicts = [];
                            teamAssignments.forEach(existingTeam => {
                              const existingMemberIds = [];
                              
                              if (existingTeam.employee1?.id) {
                                existingMemberIds.push(existingTeam.employee1.id);
                              }
                              if (existingTeam.employee2?.id) {
                                existingMemberIds.push(existingTeam.employee2.id);
                              }
                              
                              // Find conflicting members
                              const conflictingMembers = uniqueSelectedMembers.filter(memberId => 
                                existingMemberIds.includes(memberId)
                              );
                              
                              if (conflictingMembers.length > 0) {
                                const conflictingEmployees = conflictingMembers.map(memberId => {
                                  const emp = employees.find(e => e.id === memberId);
                                  return emp ? `${emp.first_name} ${emp.last_name}` : 'Unbekannt';
                                });
                                
                                conflicts.push({
                                  teamNumber: existingTeam.team_number,
                                  teamOrderType: existingTeam.order_type,
                                  employees: conflictingEmployees
                                });
                              }
                            });

                            if (conflicts.length > 0) {
                              return (
                                <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4 mb-3">
                                  <div className="flex items-start gap-2">
                                    <AlertTriangle className="w-6 h-6 text-red-600 mt-0.5 flex-shrink-0" />
                                    <div className="flex-1">
                                      <p className="text-sm font-bold text-red-800 mb-2">
                                        ⚠️ KONFLIKT ERKANNT: Mitarbeiter bereits verplant!
                                      </p>
                                      <p className="text-xs text-red-700 mb-2">
                                        Folgende Mitarbeiter dieses Teams sind bereits in anderen Teams an diesem Tag eingeplant:
                                      </p>
                                      {conflicts.map((conflict, idx) => (
                                        <div key={idx} className="bg-red-100 border border-red-300 rounded p-2 mb-2">
                                          <p className="text-xs font-semibold text-red-900">
                                            🔴 Team {conflict.teamNumber} ({conflict.teamOrderType.toUpperCase()})
                                          </p>
                                          <ul className="text-xs text-red-800 ml-4 mt-1">
                                            {conflict.employees.map((empName, empIdx) => (
                                              <li key={empIdx}>• {empName}</li>
                                            ))}
                                          </ul>
                                        </div>
                                      ))}
                                      <p className="text-xs text-red-700 font-semibold mt-2">
                                        ⚠️ Ein Mitarbeiter kann nicht gleichzeitig in mehreren Teams arbeiten!
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              );
                            }

                            return null;
                          })()}

                          {/* Warning based on team order type */}
                          {selectedTeamForAssignment.order_type === 'connect' && (
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                              <div className="flex items-start gap-2">
                                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                                <div>
                                  <p className="text-sm font-semibold text-amber-800">
                                    Nur Connect-Aufträge möglich
                                  </p>
                                  <p className="text-xs text-amber-700 mt-1">
                                    Dieses Team ist ausschließlich für Connect-Aufträge vorgesehen. GWV-Aufträge können nicht zugewiesen werden.
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}

                          {selectedTeamForAssignment.order_type === 'gwv' && (
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                              <div className="flex items-start gap-2">
                                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                                <div>
                                  <p className="text-sm font-semibold text-amber-800">
                                    Nur GWV-Aufträge möglich
                                  </p>
                                  <p className="text-xs text-amber-700 mt-1">
                                    Dieses Team ist ausschließlich für GWV-Aufträge vorgesehen. Connect-Aufträge können nicht zugewiesen werden.
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}

                          {selectedTeamForAssignment.order_type === 'beides' && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
                              <div className="flex items-start gap-2">
                                <Check className="w-5 h-5 text-green-600 mt-0.5" />
                                <div>
                                  <p className="text-sm font-semibold text-green-800">
                                    Beide Auftragsarten verfügbar
                                  </p>
                                  <p className="text-xs text-green-700 mt-1">
                                    Dieses Team kann sowohl Connect- als auch GWV-Aufträge bearbeiten.
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}

                          <div>
                            <label className={`block text-sm font-medium mb-2 ${
                              selectedTeamForAssignment.order_type === 'gwv' ? 'text-slate-400' : ''
                            }`}>
                              Connect Aufträge
                            </label>
                            <Input
                              type="number"
                              min="0"
                              value={teamAssignmentForm.connect_offtracks}
                              onChange={(e) => setTeamAssignmentForm(prev => ({
                                ...prev,
                                connect_offtracks: parseInt(e.target.value) || 0
                              }))}
                              disabled={selectedTeamForAssignment.order_type === 'gwv'}
                              className={selectedTeamForAssignment.order_type === 'gwv' ? 'bg-slate-100 cursor-not-allowed' : ''}
                            />
                            {selectedTeamForAssignment.order_type === 'gwv' && (
                              <p className="text-xs text-slate-500 mt-1">
                                ❌ Connect-Aufträge für dieses Team nicht verfügbar
                              </p>
                            )}
                          </div>

                          <div>
                            <label className={`block text-sm font-medium mb-2 ${
                              selectedTeamForAssignment.order_type === 'connect' ? 'text-slate-400' : ''
                            }`}>
                              GWV Aufträge
                            </label>
                            <Input
                              type="number"
                              min="0"
                              value={teamAssignmentForm.gwv_offtracks}
                              onChange={(e) => setTeamAssignmentForm(prev => ({
                                ...prev,
                                gwv_offtracks: parseInt(e.target.value) || 0
                              }))}
                              disabled={selectedTeamForAssignment.order_type === 'connect'}
                              className={selectedTeamForAssignment.order_type === 'connect' ? 'bg-slate-100 cursor-not-allowed' : ''}
                            />
                            {selectedTeamForAssignment.order_type === 'connect' && (
                              <p className="text-xs text-slate-500 mt-1">
                                ❌ GWV-Aufträge für dieses Team nicht verfügbar
                              </p>
                            )}
                          </div>

                          <div>
                            <label className="block text-sm font-medium mb-2">Notizen</label>
                            <Input
                              value={teamAssignmentForm.notes}
                              onChange={(e) => setTeamAssignmentForm(prev => ({
                                ...prev,
                                notes: e.target.value
                              }))}
                              placeholder="Zusätzliche Informationen..."
                            />
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {selectedTeamForAssignment && (
                    <div className="flex gap-2 mt-4">
                      <Button onClick={onAssignTeam} className="bg-blue-600 hover:bg-blue-700">
                        <Plus className="w-4 h-4 mr-2" />
                        Team zuweisen
                      </Button>
                      <Button variant="outline" onClick={() => {
                        setSelectedTeamForAssignment(null);
                        setTeamAssignmentForm({
                          team_id: '',
                          connect_offtracks: 0,
                          gwv_offtracks: 0,
                          notes: ''
                        });
                      }}>
                        Abbrechen
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Existing Team Assignments */}
              <div className="space-y-3">
                {teamAssignments.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-500">Noch keine Teams zugewiesen</p>
                  </div>
                ) : (
                  teamAssignments.map((team) => {
                    // Use employee objects directly from team assignments
                    const employee1 = team.employee1;
                    const employee2 = team.employee2;
                    
                    const employee1Conflicted = employee1 && isEmployeeConflicted(employee1.id);
                    const employee2Conflicted = employee2 && isEmployeeConflicted(employee2.id);
                    const hasConflicts = employee1Conflicted || employee2Conflicted;
                    const isPlanned = team.status === 'geplant';
                    
                    return (
                      <Card 
                        key={team.id} 
                        className={`border-l-4 ${
                          isPlanned && !hasConflicts 
                            ? 'border-l-green-500 bg-green-50' 
                            : hasConflicts 
                              ? 'border-l-red-500 bg-red-50' 
                              : 'border-l-blue-500'
                        }`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-4 mb-2">
                                <h4 className={`font-bold text-lg ${isPlanned ? 'text-green-800' : 'text-slate-900'}`}>
                                  Team {team.team_number}
                                </h4>
                                <Badge variant={team.order_type === 'connect' ? 'default' : team.order_type === 'gwv' ? 'secondary' : 'outline'}>
                                  {team.order_type.toUpperCase()}
                                </Badge>
                                {isPlanned ? (
                                  <Badge className="bg-green-600 text-white animate-pulse">
                                    ✅ GEPLANT
                                  </Badge>
                                ) : (
                                  <Badge variant="outline">
                                    Status: {team.status}
                                  </Badge>
                                )}
                                {hasConflicts && (
                                  <Badge variant="destructive" className="animate-pulse">
                                    ❌ KONFLIKT
                                  </Badge>
                                )}
                              </div>
                              
                              <div className="grid grid-cols-2 gap-4 mb-2">
                                <div>
                                  <p className="text-sm text-slate-600">Mitarbeiter 1:</p>
                                  <p className={`font-medium ${
                                    employee1Conflicted 
                                      ? 'text-red-600 bg-red-100 p-1 rounded border border-red-300' 
                                      : isPlanned 
                                        ? 'text-green-700 bg-green-100 p-1 rounded border border-green-300 font-semibold'
                                        : ''
                                  }`}>
                                    {employee1 ? (
                                      <>
                                        {employee1.name} ({employee1.employee_number})
                                        {isPlanned && !employee1Conflicted && (
                                          <span className="block text-xs text-green-700 font-bold">
                                            ✅ Erfolgreich geplant
                                          </span>
                                        )}
                                        {employee1Conflicted && (
                                          <span className="block text-xs text-red-600 font-bold">
                                            ⚠️ Bereits anderem Team zugewiesen
                                          </span>
                                        )}
                                      </>
                                    ) : (
                                      'Nicht zugewiesen'
                                    )}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-sm text-slate-600">Mitarbeiter 2:</p>
                                  <p className={`font-medium ${
                                    employee2Conflicted 
                                      ? 'text-red-600 bg-red-100 p-1 rounded border border-red-300' 
                                      : isPlanned 
                                        ? 'text-green-700 bg-green-100 p-1 rounded border border-green-300 font-semibold'
                                        : ''
                                  }`}>
                                    {employee2 ? (
                                      <>
                                        {employee2.name} ({employee2.employee_number})
                                        {isPlanned && !employee2Conflicted && (
                                          <span className="block text-xs text-green-700 font-bold">
                                            ✅ Erfolgreich geplant
                                          </span>
                                        )}
                                        {employee2Conflicted && (
                                          <span className="block text-xs text-red-600 font-bold">
                                            ⚠️ Bereits anderem Team zugewiesen
                                          </span>
                                        )}
                                      </>
                                    ) : (
                                      'Nicht zugewiesen'
                                    )}
                                  </p>
                                </div>
                              </div>
                              
                              {isPlanned && !hasConflicts && (
                                <div className="bg-green-100 border border-green-300 rounded-lg p-3 mb-2">
                                  <div className="flex items-center gap-2">
                                    <CheckCircle className="w-4 h-4 text-green-600" />
                                    <span className="text-green-800 font-semibold text-sm">
                                      Team erfolgreich für diesen Tag geplant
                                    </span>
                                  </div>
                                </div>
                              )}
                              
                              {hasConflicts && (
                                <div className="bg-red-100 border border-red-300 rounded-lg p-3 mb-2">
                                  <div className="flex items-center gap-2">
                                    <AlertTriangle className="w-4 h-4 text-red-600" />
                                    <span className="text-red-800 font-semibold text-sm">
                                      Konflikt-Warnung: Mitarbeiter ist bereits einem anderen Team zugewiesen
                                    </span>
                                  </div>
                                </div>
                              )}

                              <div className="flex gap-4 text-sm">
                                {team.connect_offtracks > 0 && (
                                  <span className={`font-medium ${isPlanned ? 'text-green-700 bg-green-100 px-2 py-1 rounded' : 'text-blue-600'}`}>
                                    Connect Auftrag: {team.connect_offtracks}
                                    {isPlanned && ' ✅'}
                                  </span>
                                )}
                                {team.gwv_offtracks > 0 && (
                                  <span className={`font-medium ${isPlanned ? 'text-green-700 bg-green-100 px-2 py-1 rounded' : 'text-green-600'}`}>
                                    GWV Auftrag: {team.gwv_offtracks}
                                    {isPlanned && ' ✅'}
                                  </span>
                                )}
                              </div>
                              
                              {team.notes && (
                                <p className="text-sm text-slate-600 mt-1">
                                  Notizen: {team.notes}
                                </p>
                              )}
                            </div>
                            
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => onDeleteTeam(team.id)}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                      </CardContent>
                    </Card>
                  );
                })
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Calendar;